import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewEncapsulation,
} from "@angular/core";
import { Subject, Subscription, of } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";

import { LocalizeService, AuthService } from "../../insa-services";
import { appStateProvider } from "../../insa-providers";
import { appConfig } from "../../../insaweb.config";

import {
  ControlTableService,
  TraderSecurityDtoModel,
  WatchlistSecurityQuoteModel,
} from "./control-table.service";
import {
  Watchlist,
  ControlTableRow,
  ControlTableSecurity,
  isFavouritesWatchlist,
  MAX_WATCHLIST_SECURITIES,
  MAX_FAVOURITES,
} from "./control-table.models";

@Component({
  selector: "app-control-table-main",
  templateUrl: "./control-table.component.html",
  styles: [require("./control-table.component.css").toString()],
  encapsulation: ViewEncapsulation.None,
})
export class ControlTableComponent implements OnInit, OnDestroy {
  watchlists: Watchlist[] = [];
  selectedWatchlistId: number | null = null;

  rows: ControlTableRow[] = [];
  rowsLoading = false;

  searchQuery = "";
  searchResults: TraderSecurityDtoModel[] = [];
  showSearchResults = false;

  liquidityCurrency = "";

  orderModalOpen = false;
  orderModalSecurity: ControlTableSecurity | null = null;
  orderModalSide: "Buy" | "Sell" = "Buy";

  depthModalOpen = false;
  depthModalSecurity: ControlTableSecurity | null = null;

  private searchSubject = new Subject<string>();
  private searchSub: Subscription;
  private watchlistsSub: Subscription;
  private pollInterval: any;
  private loadToken = 0;
  private prevPrice: { [secIndex: number]: number } = {};

  constructor(
    private _localize: LocalizeService,
    private _element: ElementRef,
    private _authService: AuthService,
    private _appStateProvider: appStateProvider,
    private _service: ControlTableService,
  ) {}

  ngOnInit(): void {
    const defaultCurrency = appConfig.initialSettings?.currency ?? "";
    this.liquidityCurrency =
      this._appStateProvider.selectedCurrency?.text || defaultCurrency;

    this.watchlistsSub = this._service.watchlists.subscribe((watchlists) => {
      this.watchlists = watchlists;
      if (
        this.selectedWatchlistId == null ||
        !watchlists.some((w) => w.WatchlistID === this.selectedWatchlistId)
      ) {
        this.selectedWatchlistId =
          watchlists.length > 0 ? watchlists[0].WatchlistID : null;
        this.loadRows();
      }
      this.reapplyLocalize();
    });

    this._service.loadWatchlists();

    this.searchSub = this.searchSubject
      .pipe(
        debounceTime(300),
        switchMap((q) => {
          if (!q || q.trim().length < 3) return of([]);
          return this._service.searchSecurities(
            q.trim(),
            this.liquidityCurrency,
          );
        }),
      )
      .subscribe((results) => {
        this.searchResults = results ?? [];
        this.showSearchResults = true;
      });

    this.pollInterval = setInterval(() => this.refreshQuotes(), 5000);
  }

  ngOnDestroy(): void {
    if (this.searchSub) this.searchSub.unsubscribe();
    if (this.watchlistsSub) this.watchlistsSub.unsubscribe();
    if (this.pollInterval) clearInterval(this.pollInterval);
  }

  get selectedWatchlist(): Watchlist | null {
    return (
      this.watchlists.find((w) => w.WatchlistID === this.selectedWatchlistId) ??
      null
    );
  }

  get isFavourites(): boolean {
    return isFavouritesWatchlist(this.selectedWatchlistId);
  }

  // Favoriti se uređuju kao i ostale liste (backend ih sprema kao trader favorite).
  get canEditSecurities(): boolean {
    return this.selectedWatchlistId != null;
  }

  get maxSecurities(): number {
    return this.isFavourites ? MAX_FAVOURITES : MAX_WATCHLIST_SECURITIES;
  }

  get isFull(): boolean {
    return this.rows.length >= this.maxSecurities;
  }

  displayName(w: Watchlist): string {
    if (isFavouritesWatchlist(w.WatchlistID))
      return this._localize.getLocalizedResource("Res3676") || "Favoriti";
    return w.WatchlistName;
  }

  get searchPlaceholder(): string {
    return (
      this._localize.getLocalizedResource("Res1120") || "npr. TSLA, AAPL..."
    );
  }

  get depthTitle(): string {
    return this._localize.getLocalizedResource("Res1130") || "Dubina tržišta";
  }

  get removeTitle(): string {
    return (
      this._localize.getLocalizedResource("Res1642") || "Ukloni sa liste"
    );
  }

  onSelectWatchlist(id: string | number): void {
    this.selectedWatchlistId = id != null ? +id : null;
    this.prevPrice = {};
    this.loadRows();
  }

  private loadRows(silent = false): void {
    const id = this.selectedWatchlistId;
    if (id == null) {
      this.rows = [];
      return;
    }
    const token = ++this.loadToken;
    if (!silent) this.rowsLoading = true;
    this._service.watchlistSecurities(id).subscribe((quotes) => {
      if (token !== this.loadToken) return;
      this.rowsLoading = false;
      this.rows = (quotes ?? [])
        .map((q) => this.rowFromQuote(q))
        .sort((a, b) => this.compareBySymbol(a, b));
      this.reapplyLocalize();
    });
  }

  private rowFromQuote(q: WatchlistSecurityQuoteModel): ControlTableRow {
    const sec = q.security;
    const l1 = q.L1Values;
    const isDomestic =
      sec.SecStockExchange === 12 || sec.SecStockExchange === 15;
    const price = l1 ? l1.price : null;
    const lastClose = l1 ? l1.lastClose : null;

    let priceDir: "" | "up" | "down" = "";
    if (price != null && lastClose != null && lastClose > 0) {
      priceDir = price > lastClose ? "up" : price < lastClose ? "down" : "";
    }
    if (price != null && this.prevPrice[sec.SecIndex] == null) {
      this.prevPrice[sec.SecIndex] =
        lastClose != null && lastClose > 0 ? lastClose : price;
    }

    return {
      security: this.toSecurity(sec),
      isDomestic,
      price,
      lastClose,
      bestBid: l1 && l1.bid != null && l1.bid > 0 ? l1.bid : null,
      bestBidQty: l1 && l1.bid != null && l1.bid > 0 ? (l1.bidQuantity ?? 0) : null,
      bestAsk: l1 && l1.ask != null && l1.ask > 0 ? l1.ask : null,
      bestAskQty: l1 && l1.ask != null && l1.ask > 0 ? (l1.askQuantity ?? 0) : null,
      open: l1 ? l1.open : null,
      high: l1 ? l1.high : null,
      low: l1 ? l1.low : null,
      volume: l1 ? l1.volume : null,
      tradingPhase: l1 ? l1.tradingPhase : null,
      priceRange: l1 ? l1.priceRange : null,
      currency: (l1 && l1.currency) || sec.SecCurrSymbol || null,
      priceDir,
      loading: false,
    };
  }

  private refreshQuotes(): void {
    for (const row of this.rows) {
      this._service.realTimeL1(row.security).subscribe((data) => {
        if (!data || data.length === 0) return;
        const l1 = data[0];
        const idx = row.security.SecIndex;
        const newPrice = l1.price;

        if (newPrice != null) {
          const prev = this.prevPrice[idx];
          if (prev != null) {
            if (newPrice > prev) row.priceDir = "up";
            else if (newPrice < prev) row.priceDir = "down";
          }
          this.prevPrice[idx] = newPrice;
          row.price = newPrice;
        }

        if (l1.lastClose != null && l1.lastClose > 0)
          row.lastClose = l1.lastClose;
        row.bestBid = l1.bid != null && l1.bid > 0 ? l1.bid : null;
        row.bestBidQty = row.bestBid != null ? (l1.bidQuantity ?? 0) : null;
        row.bestAsk = l1.ask != null && l1.ask > 0 ? l1.ask : null;
        row.bestAskQty = row.bestAsk != null ? (l1.askQuantity ?? 0) : null;
        if (l1.open != null) row.open = l1.open;
        if (l1.high != null) row.high = l1.high;
        if (l1.low != null) row.low = l1.low;
        if (l1.volume != null) row.volume = l1.volume;
        if (l1.tradingPhase != null) row.tradingPhase = l1.tradingPhase;
        if (l1.priceRange != null) row.priceRange = l1.priceRange;
        if (l1.currency) row.currency = l1.currency;
      });
    }
  }

  onSearch(): void {
    this.searchSubject.next(this.searchQuery);
  }

  hideSearchResults(): void {
    setTimeout(() => {
      this.showSearchResults = false;
    }, 200);
  }

  addSecurity(sec: TraderSecurityDtoModel): void {
    if (!this.canEditSecurities || this.selectedWatchlistId == null) return;
    if (this.isAlreadyAdded(sec)) return;
    if (this.isFull) return;
    this.searchQuery = "";
    this.searchResults = [];
    this.showSearchResults = false;
    this._service
      .addSecurity(this.selectedWatchlistId, sec)
      .subscribe(() => this.loadRows(true));
  }

  removeSecurity(row: ControlTableRow): void {
    if (!this.canEditSecurities || this.selectedWatchlistId == null) return;
    this._service
      .removeSecurity(this.selectedWatchlistId, row.security)
      .subscribe(() => this.loadRows(true));
  }

  isAlreadyAdded(sec: TraderSecurityDtoModel): boolean {
    return this.rows.some((r) => r.security.SecIndex === sec.SecIndex);
  }

  openOrder(row: ControlTableRow, side: "Buy" | "Sell"): void {
    this.orderModalSecurity = row.security;
    this.orderModalSide = side;
    this.orderModalOpen = true;
    this.reapplyLocalize();
  }

  closeOrderModal(): void {
    this.orderModalOpen = false;
    this.orderModalSecurity = null;
  }

  openDepth(row: ControlTableRow): void {
    if (!row.isDomestic) return;
    this.depthModalSecurity = row.security;
    this.depthModalOpen = true;
    this.reapplyLocalize();
  }

  closeDepthModal(): void {
    this.depthModalOpen = false;
    this.depthModalSecurity = null;
  }

  private toSecurity(sec: TraderSecurityDtoModel): ControlTableSecurity {
    return {
      SecIndex: sec.SecIndex,
      SecNumber: sec.SecNumber,
      SecISIN: sec.SecISIN,
      SecStockExchange: sec.SecStockExchange,
      SecSymbol: sec.SecSymbol,
      SecName: sec.SecName,
      SecCurrSymbol: sec.SecCurrSymbol,
      FxRate: sec.FxRate,
      StockExchangeCode: sec.StockExchangeCode,
      StockExchangeName: sec.StockExchangeName,
      EODHDStockExchangeCode: sec.EODHDStockExchangeCode,
      SecType: sec.SecType,
      SecCurr: sec.SecCurr,
      SecFactor: sec.SecFactor,
      SecContractSize: sec.SecContractSize,
    };
  }

  formatN(value: number | null | undefined, decimals = 2): string {
    if (value === null || value === undefined || isNaN(value as number))
      return "";
    return (kendo as any).toString(value, "n" + decimals);
  }

  // Backend šalje raspon kao sirovi string ("26180-48620") - formatiramo
  // obje granice kao i ostale cijene; nepoznat format prikažemo kakav jest.
  formatRange(range: string | null | undefined): string {
    if (!range || !range.trim()) return "—";
    const match = range.trim().match(/^([\d.]+)\s*-\s*([\d.]+)$/);
    if (!match) return range;
    return `${this.formatN(parseFloat(match[1]))} – ${this.formatN(
      parseFloat(match[2]),
    )}`;
  }

  symbolOf(row: ControlTableRow): string {
    return (
      row.security.SecSymbol?.trim() || row.security.SecNumber?.trim() || ""
    );
  }

  // Abecedno po simbolu hartije (SecSymbol, fallback SecNumber).
  private compareBySymbol(a: ControlTableRow, b: ControlTableRow): number {
    return this.symbolOf(a).localeCompare(this.symbolOf(b), "sr-Latn", {
      sensitivity: "base",
      numeric: true,
    });
  }

  priceClass(row: ControlTableRow): string {
    if (row.priceDir === "up") return "ct-price-up";
    if (row.priceDir === "down") return "ct-price-down";
    return "";
  }

  private reapplyLocalize(): void {
    setTimeout(
      () => this._localize.applyTranslations(this._element.nativeElement),
      0,
    );
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-control-table/control-table.component.ts